import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Settings as SettingsIcon, User, Key, Server, Save, ExternalLink, Lock, AlertTriangle, Euro, Zap, Wrench, RefreshCw, Trash2, Calculator, Activity } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const TARIFF_TYPES = {
    simples: { name: 'Simples', description: 'Preço único 24h/dia', periods: ['simples'] },
    bi_horario_semanal: { name: 'Bi-horário Semanal', description: 'Vazio e Cheias (semanal)', periods: ['vazio', 'cheias'] },
    bi_horario_diario: { name: 'Bi-horário Diário', description: 'Vazio (22h-8h) e Cheias (8h-22h)', periods: ['vazio', 'cheias'] },
    tri_horario_semanal: { name: 'Tri-horário Semanal', description: 'Vazio, Cheias e Ponta (semanal)', periods: ['vazio', 'cheias', 'ponta'] },
    tri_horario_diario: { name: 'Tri-horário Diário', description: 'Vazio, Cheias e Ponta (diário)', periods: ['vazio', 'cheias', 'ponta'] }
};

const PERIOD_LABELS = {
    simples: 'Preço Único',
    vazio: 'Vazio',
    cheias: 'Cheias',
    ponta: 'Ponta'
};

export default function Settings() {
    const { api, user, isAdmin } = useAuth();
    const [loading, setLoading] = useState(false);
    const [profileLoading, setProfileLoading] = useState(false);
    const [tariffLoading, setTariffLoading] = useState(false);
    const [shellyCredentials, setShellyCredentials] = useState({
        shelly_server: '',
        auth_key: '',
        has_auth_key: false
    });
    const [newAuthKey, setNewAuthKey] = useState('');
    
    // Profile form
    const [profileData, setProfileData] = useState({
        name: '',
        password: '',
        confirmPassword: ''
    });
    
    // Tariff settings
    const [tariffType, setTariffType] = useState('simples');
    const [tariffPrices, setTariffPrices] = useState({ simples: '' });
    
    // Maintenance states
    const [maintenanceLoading, setMaintenanceLoading] = useState({});
    const [maintenanceResults, setMaintenanceResults] = useState({});
    const [devices, setDevices] = useState([]);
    const [clearDeviceId, setClearDeviceId] = useState('');
    const [clearBeforeDate, setClearBeforeDate] = useState('');

    useEffect(() => {
        if (user) {
            setProfileData(prev => ({
                ...prev,
                name: user.name || ''
            }));
        }
    }, [user]);

    const fetchCredentials = async () => {
        if (!isAdmin) return;
        
        try {
            const response = await api.get('/auth/shelly-credentials');
            setShellyCredentials({
                shelly_server: response.data.shelly_server || '',
                has_auth_key: response.data.has_auth_key,
                auth_key: ''
            });
        } catch (error) {
            console.error('Error fetching credentials:', error);
        }
    };

    const fetchTariffSettings = async () => {
        try {
            const response = await api.get('/settings/tariff');
            setTariffType(response.data.tariff_type || 'simples');
            const prices = response.data.tariff_prices || {};
            // Convert numbers to strings for input fields
            const pricesStr = {};
            Object.keys(prices).forEach(k => {
                pricesStr[k] = prices[k]?.toString() || '';
            });
            setTariffPrices(pricesStr);
        } catch (error) {
            console.error('Error fetching tariff settings:', error);
        }
    };

    useEffect(() => {
        fetchCredentials();
        fetchTariffSettings();
        fetchDevices();
    }, [isAdmin]);

    const fetchDevices = async () => {
        if (!isAdmin) return;
        try {
            const response = await api.get('/devices');
            setDevices(response.data);
        } catch (error) {
            console.error('Error fetching devices:', error);
        }
    };

    const runMaintenance = async (operation, params = {}) => {
        setMaintenanceLoading(prev => ({ ...prev, [operation]: true }));
        setMaintenanceResults(prev => ({ ...prev, [operation]: null }));
        
        try {
            let response;
            if (operation === 'clear-readings') {
                const queryParams = new URLSearchParams();
                if (params.device_id) queryParams.append('device_id', params.device_id);
                if (params.before_date) queryParams.append('before_date', params.before_date);
                response = await api.delete(`/maintenance/${operation}?${queryParams.toString()}`);
            } else {
                response = await api.post(`/maintenance/${operation}`, params);
            }
            
            setMaintenanceResults(prev => ({ ...prev, [operation]: response.data }));
            toast.success(response.data.message || 'Operação concluída com sucesso');
        } catch (error) {
            console.error(`Error running ${operation}:`, error);
            toast.error(error.response?.data?.detail || 'Erro ao executar operação');
            setMaintenanceResults(prev => ({ 
                ...prev, 
                [operation]: { success: false, message: error.response?.data?.detail || 'Erro' } 
            }));
        } finally {
            setMaintenanceLoading(prev => ({ ...prev, [operation]: false }));
        }
    };

    const handleSaveShellyCredentials = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            await api.put('/auth/shelly-credentials', {
                shelly_server: shellyCredentials.shelly_server,
                auth_key: newAuthKey || shellyCredentials.auth_key
            });
            toast.success('Credenciais Shelly atualizadas com sucesso');
            setNewAuthKey('');
            fetchCredentials();
        } catch (error) {
            console.error('Error saving credentials:', error);
            toast.error(error.response?.data?.detail || 'Erro ao guardar credenciais');
        } finally {
            setLoading(false);
        }
    };

    const handleTariffTypeChange = (newType) => {
        setTariffType(newType);
        // Initialize prices for new tariff type
        const periods = TARIFF_TYPES[newType].periods;
        const newPrices = {};
        periods.forEach(p => {
            newPrices[p] = tariffPrices[p] || '';
        });
        setTariffPrices(newPrices);
    };

    const handleSaveTariff = async (e) => {
        e.preventDefault();
        
        const periods = TARIFF_TYPES[tariffType].periods;
        const prices = {};
        
        for (const period of periods) {
            const price = parseFloat(tariffPrices[period]);
            if (isNaN(price) || price <= 0) {
                toast.error(`Introduza um preço válido para "${PERIOD_LABELS[period]}"`);
                return;
            }
            prices[period] = price;
        }
        
        setTariffLoading(true);
        
        try {
            await api.put('/settings/tariff', { 
                tariff_type: tariffType,
                prices: prices
            });
            toast.success('Tarifário atualizado com sucesso');
        } catch (error) {
            console.error('Error saving tariff:', error);
            toast.error(error.response?.data?.detail || 'Erro ao guardar tarifário');
        } finally {
            setTariffLoading(false);
        }
    };

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        
        if (profileData.password && profileData.password !== profileData.confirmPassword) {
            toast.error('As passwords não coincidem');
            return;
        }
        
        if (profileData.password && profileData.password.length < 6) {
            toast.error('A password deve ter pelo menos 6 caracteres');
            return;
        }
        
        setProfileLoading(true);
        
        try {
            const updateData = {};
            if (profileData.name && profileData.name !== user?.name) {
                updateData.name = profileData.name;
            }
            if (profileData.password) {
                updateData.password = profileData.password;
            }
            
            if (Object.keys(updateData).length === 0) {
                toast.info('Nenhuma alteração para guardar');
                setProfileLoading(false);
                return;
            }
            
            await api.put('/auth/profile', updateData);
            toast.success('Perfil atualizado com sucesso');
            setProfileData(prev => ({ ...prev, password: '', confirmPassword: '' }));
            
            // Refresh user data
            window.location.reload();
        } catch (error) {
            console.error('Error saving profile:', error);
            toast.error(error.response?.data?.detail || 'Erro ao atualizar perfil');
        } finally {
            setProfileLoading(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="settings-page">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold font-heading text-slate-900">Definições</h1>
                <p className="text-slate-500">Configure a sua conta e integrações</p>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
                {/* User Profile - Editable */}
                <Card data-testid="user-profile-card">
                    <CardHeader>
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-slate-100">
                                <User className="w-5 h-5 text-slate-600" />
                            </div>
                            <div>
                                <CardTitle className="text-lg font-heading">O Meu Perfil</CardTitle>
                                <CardDescription>Atualize o seu nome e password</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSaveProfile} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="profile_name">Nome</Label>
                                <Input
                                    id="profile_name"
                                    value={profileData.name}
                                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                                    data-testid="profile-name-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Email</Label>
                                <Input value={user?.email || ''} disabled className="bg-slate-50" />
                                <p className="text-xs text-slate-500">O email não pode ser alterado</p>
                            </div>
                            
                            <Separator />
                            
                            <div className="space-y-2">
                                <Label htmlFor="profile_password">Nova Password</Label>
                                <Input
                                    id="profile_password"
                                    type="password"
                                    placeholder="Deixe em branco para manter a atual"
                                    value={profileData.password}
                                    onChange={(e) => setProfileData({ ...profileData, password: e.target.value })}
                                    data-testid="profile-password-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="profile_confirm_password">Confirmar Nova Password</Label>
                                <Input
                                    id="profile_confirm_password"
                                    type="password"
                                    placeholder="Confirme a nova password"
                                    value={profileData.confirmPassword}
                                    onChange={(e) => setProfileData({ ...profileData, confirmPassword: e.target.value })}
                                    data-testid="profile-confirm-password-input"
                                />
                            </div>
                            
                            <Button
                                type="submit"
                                className="bg-emerald-600 hover:bg-emerald-700"
                                disabled={profileLoading}
                                data-testid="save-profile-btn"
                            >
                                <Save className="w-4 h-4 mr-2" />
                                {profileLoading ? 'A guardar...' : 'Guardar Perfil'}
                            </Button>
                        </form>
                    </CardContent>
                </Card>

                {/* Shelly Cloud Credentials - Admin Only */}
                {isAdmin ? (
                    <Card data-testid="shelly-credentials-card">
                        <CardHeader>
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-emerald-50">
                                    <Server className="w-5 h-5 text-emerald-600" />
                                </div>
                                <div>
                                    <CardTitle className="text-lg font-heading">Shelly Cloud</CardTitle>
                                    <CardDescription>Configure a integração com Shelly Cloud</CardDescription>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSaveShellyCredentials} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="shelly_server">Servidor Shelly Cloud</Label>
                                    <Input
                                        id="shelly_server"
                                        placeholder="https://shelly-55-eu.shelly.cloud"
                                        value={shellyCredentials.shelly_server}
                                        onChange={(e) => setShellyCredentials({ ...shellyCredentials, shelly_server: e.target.value })}
                                        data-testid="shelly-server-input"
                                    />
                                    <p className="text-xs text-slate-500">
                                        O servidor depende da sua região. Consulte a app Shelly para obter o endereço correto.
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="auth_key">Auth Key</Label>
                                    <Input
                                        id="auth_key"
                                        type="password"
                                        placeholder={shellyCredentials.has_auth_key ? '••••••••••••' : 'Introduza a sua Auth Key'}
                                        value={newAuthKey}
                                        onChange={(e) => setNewAuthKey(e.target.value)}
                                        data-testid="shelly-auth-key-input"
                                    />
                                    <p className="text-xs text-slate-500">
                                        {shellyCredentials.has_auth_key 
                                            ? 'Auth Key configurada. Deixe em branco para manter a atual.'
                                            : 'Obtenha a Auth Key em User Settings > Authorization cloud key na app Shelly.'
                                        }
                                    </p>
                                </div>

                                <div className="flex items-center gap-3 pt-2">
                                    <Button
                                        type="submit"
                                        className="bg-emerald-600 hover:bg-emerald-700"
                                        disabled={loading}
                                        data-testid="save-shelly-btn"
                                    >
                                        <Save className="w-4 h-4 mr-2" />
                                        {loading ? 'A guardar...' : 'Guardar Credenciais'}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        asChild
                                    >
                                        <a
                                            href="https://home.shelly.cloud"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <ExternalLink className="w-4 h-4 mr-2" />
                                            Shelly Cloud
                                        </a>
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                ) : (
                    <Card data-testid="shelly-credentials-restricted">
                        <CardHeader>
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-slate-100">
                                    <Lock className="w-5 h-5 text-slate-400" />
                                </div>
                                <div>
                                    <CardTitle className="text-lg font-heading text-slate-500">Shelly Cloud</CardTitle>
                                    <CardDescription>Integração com Shelly Cloud</CardDescription>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <Alert>
                                <AlertTriangle className="w-4 h-4" />
                                <AlertDescription>
                                    Apenas administradores podem configurar as credenciais da Shelly Cloud.
                                    Contacte o administrador da sua empresa se precisar de configurar esta integração.
                                </AlertDescription>
                            </Alert>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Tariff Configuration - Admin Only */}
            {isAdmin && (
                <Card data-testid="tariff-card">
                    <CardHeader>
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-amber-50">
                                <Zap className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                                <CardTitle className="text-lg font-heading">Tarifário de Energia</CardTitle>
                                <CardDescription>Configure o tipo de tarifário e preços por período</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSaveTariff} className="space-y-6">
                            <div className="space-y-2">
                                <Label htmlFor="tariff_type">Tipo de Tarifário</Label>
                                <Select value={tariffType} onValueChange={handleTariffTypeChange}>
                                    <SelectTrigger className="w-full max-w-md" data-testid="tariff-type-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(TARIFF_TYPES).map(([key, value]) => (
                                            <SelectItem key={key} value={key}>
                                                <div className="flex flex-col">
                                                    <span className="font-medium">{value.name}</span>
                                                    <span className="text-xs text-slate-500">{value.description}</span>
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            <Separator />
                            
                            <div className="space-y-4">
                                <Label>Preços por Período (€/kWh)</Label>
                                <div className="grid gap-4 md:grid-cols-3">
                                    {TARIFF_TYPES[tariffType].periods.map(period => (
                                        <div key={period} className="space-y-2">
                                            <Label htmlFor={`price_${period}`} className="text-sm">
                                                {PERIOD_LABELS[period]}
                                            </Label>
                                            <div className="flex items-center gap-2">
                                                <Input
                                                    id={`price_${period}`}
                                                    type="number"
                                                    step="0.0001"
                                                    min="0.0001"
                                                    placeholder="0.1500"
                                                    value={tariffPrices[period] || ''}
                                                    onChange={(e) => setTariffPrices(prev => ({
                                                        ...prev,
                                                        [period]: e.target.value
                                                    }))}
                                                    className="w-full"
                                                    data-testid={`price-${period}-input`}
                                                />
                                                <span className="text-slate-500 text-sm">€</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                
                                {/* Tariff Info */}
                                {tariffType !== 'simples' && (
                                    <div className="mt-4 p-4 bg-slate-50 rounded-lg text-sm">
                                        <h4 className="font-semibold text-slate-700 mb-2">Informação do Tarifário</h4>
                                        {tariffType === 'bi_horario_diario' && (
                                            <ul className="space-y-1 text-slate-600">
                                                <li><strong>Vazio:</strong> 22h às 8h (todos os dias)</li>
                                                <li><strong>Cheias:</strong> 8h às 22h (todos os dias)</li>
                                            </ul>
                                        )}
                                        {tariffType === 'bi_horario_semanal' && (
                                            <ul className="space-y-1 text-slate-600">
                                                <li><strong>Dias úteis:</strong> Vazio 0h-7h | Cheias 7h-24h</li>
                                                <li><strong>Sábados (verão):</strong> Vazio 0h-9h, 14h-20h, 22h-24h | Cheias resto</li>
                                                <li><strong>Sábados (inverno):</strong> Vazio 0h-9h30, 13h-18h30, 22h-24h | Cheias resto</li>
                                                <li><strong>Domingos:</strong> Vazio 0h-24h</li>
                                            </ul>
                                        )}
                                        {tariffType === 'tri_horario_diario' && (
                                            <ul className="space-y-1 text-slate-600">
                                                <li><strong>Vazio:</strong> 22h às 8h</li>
                                                <li><strong>Cheias (verão):</strong> 8h-10h30, 13h-19h30, 21h-22h</li>
                                                <li><strong>Ponta (verão):</strong> 10h30-13h, 19h30-21h</li>
                                                <li><strong>Cheias (inverno):</strong> 8h-9h, 10h30-18h, 20h30-22h</li>
                                                <li><strong>Ponta (inverno):</strong> 9h-10h30, 18h-20h30</li>
                                            </ul>
                                        )}
                                        {tariffType === 'tri_horario_semanal' && (
                                            <ul className="space-y-1 text-slate-600">
                                                <li><strong>Dias úteis (verão):</strong> Vazio 0h-7h | Cheias 7h-9h15, 12h15-24h | Ponta 9h15-12h15</li>
                                                <li><strong>Dias úteis (inverno):</strong> Vazio 0h-7h | Cheias 7h-9h30, 12h-18h30, 21h-24h | Ponta 9h30-12h, 18h30-21h</li>
                                                <li><strong>Sábados:</strong> Mesmo que bi-horário semanal (sem ponta)</li>
                                                <li><strong>Domingos:</strong> Vazio 0h-24h</li>
                                            </ul>
                                        )}
                                        <p className="mt-2 text-xs text-slate-500">
                                            * Verão: último domingo de Março a último domingo de Outubro
                                        </p>
                                    </div>
                                )}
                            </div>
                            
                            <Button
                                type="submit"
                                className="bg-amber-600 hover:bg-amber-700"
                                disabled={tariffLoading}
                                data-testid="save-tariff-btn"
                            >
                                <Save className="w-4 h-4 mr-2" />
                                {tariffLoading ? 'A guardar...' : 'Guardar Tarifário'}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            )}

            {/* Advanced Maintenance - Admin Only */}
            {isAdmin && (
                <Card data-testid="maintenance-card" className="border-orange-200 bg-orange-50/30">
                    <CardHeader>
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-orange-100">
                                <Wrench className="w-5 h-5 text-orange-600" />
                            </div>
                            <div>
                                <CardTitle className="text-lg font-heading">Manutenção Avançada</CardTitle>
                                <CardDescription>Recalcular dados históricos e limpar registos</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <Alert className="border-orange-300 bg-orange-100">
                            <AlertTriangle className="w-4 h-4 text-orange-600" />
                            <AlertDescription className="text-orange-800">
                                Estas operações podem demorar algum tempo e afetam dados históricos. 
                                Execute apenas quando necessário (ex: após novo deploy ou correção de configurações).
                            </AlertDescription>
                        </Alert>

                        {/* Recalculate Currents */}
                        <div className="p-4 border rounded-lg bg-white space-y-3">
                            <div className="flex items-start gap-3">
                                <Activity className="w-5 h-5 text-blue-600 mt-0.5" />
                                <div className="flex-1">
                                    <h4 className="font-semibold text-slate-800">Recalcular Correntes</h4>
                                    <p className="text-sm text-slate-600 mt-1">
                                        Corrige valores negativos de corrente e calcula corrente média/máxima para dispositivos 3EM.
                                    </p>
                                    {maintenanceResults['recalculate-currents'] && (
                                        <div className={`mt-2 p-2 rounded text-sm ${maintenanceResults['recalculate-currents'].success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                            {maintenanceResults['recalculate-currents'].success ? (
                                                <>
                                                    ✓ Processados: {maintenanceResults['recalculate-currents'].records_processed} | 
                                                    Atualizados: {maintenanceResults['recalculate-currents'].records_updated}
                                                    {maintenanceResults['recalculate-currents'].details && (
                                                        <span className="block mt-1 text-xs">
                                                            Negativos corrigidos: {maintenanceResults['recalculate-currents'].details.fixed_negative_currents} | 
                                                            Avg/Max 3EM: {maintenanceResults['recalculate-currents'].details.calculated_avg_max_3em}
                                                        </span>
                                                    )}
                                                </>
                                            ) : maintenanceResults['recalculate-currents'].message}
                                        </div>
                                    )}
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => runMaintenance('recalculate-currents')}
                                    disabled={maintenanceLoading['recalculate-currents']}
                                    data-testid="maintenance-currents-btn"
                                >
                                    {maintenanceLoading['recalculate-currents'] ? (
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                    ) : (
                                        'Executar'
                                    )}
                                </Button>
                            </div>
                        </div>

                        {/* Recalculate Inversions */}
                        <div className="p-4 border rounded-lg bg-white space-y-3">
                            <div className="flex items-start gap-3">
                                <RefreshCw className="w-5 h-5 text-purple-600 mt-0.5" />
                                <div className="flex-1">
                                    <h4 className="font-semibold text-slate-800">Recalcular Inversões</h4>
                                    <p className="text-sm text-slate-600 mt-1">
                                        Aplica as configurações atuais de inversão de sinal a todos os registos históricos.
                                    </p>
                                    {maintenanceResults['recalculate-inversions'] && (
                                        <div className={`mt-2 p-2 rounded text-sm ${maintenanceResults['recalculate-inversions'].success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                            {maintenanceResults['recalculate-inversions'].success ? (
                                                <>
                                                    ✓ Processados: {maintenanceResults['recalculate-inversions'].records_processed} | 
                                                    Atualizados: {maintenanceResults['recalculate-inversions'].records_updated}
                                                </>
                                            ) : maintenanceResults['recalculate-inversions'].message}
                                        </div>
                                    )}
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => runMaintenance('recalculate-inversions')}
                                    disabled={maintenanceLoading['recalculate-inversions']}
                                    data-testid="maintenance-inversions-btn"
                                >
                                    {maintenanceLoading['recalculate-inversions'] ? (
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                    ) : (
                                        'Executar'
                                    )}
                                </Button>
                            </div>
                        </div>

                        {/* Recalculate Costs */}
                        <div className="p-4 border rounded-lg bg-white space-y-3">
                            <div className="flex items-start gap-3">
                                <Calculator className="w-5 h-5 text-amber-600 mt-0.5" />
                                <div className="flex-1">
                                    <h4 className="font-semibold text-slate-800">Recalcular Custos</h4>
                                    <p className="text-sm text-slate-600 mt-1">
                                        Recalcula os custos de energia de todos os registos com base no tarifário atual.
                                    </p>
                                    {maintenanceResults['recalculate-costs'] && (
                                        <div className={`mt-2 p-2 rounded text-sm ${maintenanceResults['recalculate-costs'].success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                            {maintenanceResults['recalculate-costs'].success ? (
                                                <>
                                                    ✓ Processados: {maintenanceResults['recalculate-costs'].records_processed} | 
                                                    Atualizados: {maintenanceResults['recalculate-costs'].records_updated}
                                                    {maintenanceResults['recalculate-costs'].details && (
                                                        <span className="block mt-1 text-xs">
                                                            Tarifário: {maintenanceResults['recalculate-costs'].details.tariff_type}
                                                        </span>
                                                    )}
                                                </>
                                            ) : maintenanceResults['recalculate-costs'].message}
                                        </div>
                                    )}
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => runMaintenance('recalculate-costs')}
                                    disabled={maintenanceLoading['recalculate-costs']}
                                    data-testid="maintenance-costs-btn"
                                >
                                    {maintenanceLoading['recalculate-costs'] ? (
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                    ) : (
                                        'Executar'
                                    )}
                                </Button>
                            </div>
                        </div>

                        {/* Clean Anomalies */}
                        <div className="p-4 border rounded-lg bg-white space-y-3">
                            <div className="flex items-start gap-3">
                                <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                                <div className="flex-1">
                                    <h4 className="font-semibold text-slate-800">Corrigir Anomalias</h4>
                                    <p className="text-sm text-slate-600 mt-1">
                                        Detecta e corrige picos anormais de energia (>100 kWh/leitura), estimando valores com base na potência.
                                    </p>
                                    {maintenanceResults['clean-anomalies'] && (
                                        <div className={`mt-2 p-2 rounded text-sm ${maintenanceResults['clean-anomalies'].success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                            {maintenanceResults['clean-anomalies'].success ? (
                                                <>
                                                    ✓ Processados: {maintenanceResults['clean-anomalies'].records_processed} | 
                                                    Anomalias encontradas: {maintenanceResults['clean-anomalies'].details?.anomalies_found || 0} |
                                                    Corrigidas: {maintenanceResults['clean-anomalies'].details?.anomalies_fixed || 0}
                                                </>
                                            ) : maintenanceResults['clean-anomalies'].message}
                                        </div>
                                    )}
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => runMaintenance('clean-anomalies')}
                                    disabled={maintenanceLoading['clean-anomalies']}
                                    data-testid="maintenance-anomalies-btn"
                                >
                                    {maintenanceLoading['clean-anomalies'] ? (
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                    ) : (
                                        'Executar'
                                    )}
                                </Button>
                            </div>
                        </div>

                        <Separator />

                        {/* Clear Readings - Danger Zone */}
                        <div className="p-4 border border-red-200 rounded-lg bg-red-50/50 space-y-4">
                            <div className="flex items-start gap-3">
                                <Trash2 className="w-5 h-5 text-red-600 mt-0.5" />
                                <div className="flex-1">
                                    <h4 className="font-semibold text-red-800">Limpar Registos</h4>
                                    <p className="text-sm text-red-700 mt-1">
                                        Elimina registos permanentemente. Esta ação não pode ser revertida.
                                    </p>
                                </div>
                            </div>
                            
                            <div className="grid gap-4 md:grid-cols-2">
                                <div className="space-y-2">
                                    <Label className="text-sm">Dispositivo (opcional)</Label>
                                    <Select 
                                        value={clearDeviceId || "all"} 
                                        onValueChange={(v) => setClearDeviceId(v === "all" ? "" : v)}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Todos os dispositivos" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">Todos os dispositivos</SelectItem>
                                            {devices.map(d => (
                                                <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-sm">Antes de (opcional)</Label>
                                    <Input
                                        type="datetime-local"
                                        value={clearBeforeDate}
                                        onChange={(e) => setClearBeforeDate(e.target.value)}
                                        data-testid="clear-before-date"
                                    />
                                </div>
                            </div>

                            {maintenanceResults['clear-readings'] && (
                                <div className={`p-2 rounded text-sm ${maintenanceResults['clear-readings'].success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                    {maintenanceResults['clear-readings'].success ? (
                                        <>✓ Registos eliminados: {maintenanceResults['clear-readings'].details?.deleted_count || 0}</>
                                    ) : maintenanceResults['clear-readings'].message}
                                </div>
                            )}

                            <AlertDialog>
                                <AlertDialogTrigger asChild>
                                    <Button
                                        variant="destructive"
                                        size="sm"
                                        disabled={maintenanceLoading['clear-readings']}
                                        data-testid="maintenance-clear-btn"
                                    >
                                        {maintenanceLoading['clear-readings'] ? (
                                            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                                        ) : (
                                            <Trash2 className="w-4 h-4 mr-2" />
                                        )}
                                        Eliminar Registos
                                    </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                    <AlertDialogHeader>
                                        <AlertDialogTitle>Tem a certeza?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                            Esta ação vai eliminar permanentemente os registos selecionados.
                                            {!clearDeviceId && !clearBeforeDate && (
                                                <span className="block mt-2 font-semibold text-red-600">
                                                    ATENÇÃO: Sem filtros, TODOS os registos serão eliminados!
                                                </span>
                                            )}
                                            {clearDeviceId && (
                                                <span className="block mt-1">
                                                    Dispositivo: {devices.find(d => d.id === clearDeviceId)?.name || clearDeviceId}
                                                </span>
                                            )}
                                            {clearBeforeDate && (
                                                <span className="block mt-1">
                                                    Antes de: {new Date(clearBeforeDate).toLocaleString('pt-PT')}
                                                </span>
                                            )}
                                        </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                        <AlertDialogAction
                                            className="bg-red-600 hover:bg-red-700"
                                            onClick={() => runMaintenance('clear-readings', {
                                                device_id: clearDeviceId || undefined,
                                                before_date: clearBeforeDate ? new Date(clearBeforeDate).toISOString() : undefined
                                            })}
                                        >
                                            Eliminar
                                        </AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Help Section - Admin Only */}
            {isAdmin && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg font-heading">Como configurar a integração Shelly</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-3">
                            <div className="space-y-2">
                                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold">
                                    1
                                </div>
                                <h4 className="font-semibold">Aceda ao Shelly Cloud</h4>
                                <p className="text-sm text-slate-500">
                                    Faça login em home.shelly.cloud ou na app Shelly com a sua conta.
                                </p>
                            </div>
                            <div className="space-y-2">
                                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold">
                                    2
                                </div>
                                <h4 className="font-semibold">Obtenha a Auth Key</h4>
                                <p className="text-sm text-slate-500">
                                    Vá a User Settings e copie a "Authorization cloud key".
                                </p>
                            </div>
                            <div className="space-y-2">
                                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold">
                                    3
                                </div>
                                <h4 className="font-semibold">Configure aqui</h4>
                                <p className="text-sm text-slate-500">
                                    Cole a Auth Key e o servidor Shelly correto para a sua região.
                                </p>
                            </div>
                        </div>
                        
                        <Separator />
                        
                        <div>
                            <h4 className="font-semibold mb-2">Servidores Shelly por região:</h4>
                            <ul className="text-sm text-slate-600 space-y-1">
                                <li><code className="bg-slate-100 px-2 py-0.5 rounded">https://shelly-55-eu.shelly.cloud</code> - Europa</li>
                                <li><code className="bg-slate-100 px-2 py-0.5 rounded">https://shelly-14-us.shelly.cloud</code> - Estados Unidos</li>
                                <li><code className="bg-slate-100 px-2 py-0.5 rounded">https://shelly-103-us.shelly.cloud</code> - Estados Unidos (alternativo)</li>
                            </ul>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
